import React, { useState } from "react";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";

const statusLabels = {
    age: "reg.status.reason.age",
    class: "reg.status.reason.class",
    licence: "reg.status.reason.licence",
    style: "reg.status.reason.style",
    limits: "reg.status.reason.limits",
    count: "reg.status.reason.count",
};

const StatusChip = ({ value, t }) =>
    value ? (
        <Chip
            icon={<CheckCircleIcon />}
            label={t("reg.status.validation_ok")}
            color="success"
            size="small"
            variant="outlined"
        />
    ) : (
        <Chip
            icon={<CancelIcon />}
            label={t("reg.status.validation_failed")}
            color="error"
            size="small"
        />
    );

const AgeDetailsSection = ({ ageDetails, t }) => {
    if (!ageDetails || ageDetails.ok) return null;

    const { isTeam, participantMinAge, participantMaxAge, membersCount, minAge, maxAge } = ageDetails;

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t("reg.status.age_details")}:
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                <Table size="small">
                    <TableBody>
                        <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>{t("reg.status.age_participant_min")}</TableCell>
                            <TableCell>{participantMinAge}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>{t("reg.status.age_participant_max")}</TableCell>
                            <TableCell>{participantMaxAge}</TableCell>
                        </TableRow>
                        {isTeam && (
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>{t("reg.status.age_team_members_count")}</TableCell>
                                <TableCell>{membersCount}</TableCell>
                            </TableRow>
                        )}

                        {minAge.limit !== null && (
                            <>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: "bold" }}>{t("reg.status.age_min_limit")}</TableCell>
                                    <TableCell>{minAge.limit}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: "bold" }}>{t("reg.status.age_min_ok")}</TableCell>
                                    <TableCell><StatusChip value={minAge.ok} t={t} /></TableCell>
                                </TableRow>

                                {isTeam && minAge.details && !minAge.ok && (
                                    <>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: "bold" }}>{t("reg.status.age_under_age_count")}</TableCell>
                                            <TableCell>
                                                <Typography
                                                    component="span"
                                                    color={minAge.details.numberOfUnderAge > minAge.details.allowedUnderAge ? "error" : "text.primary"}
                                                    sx={{ fontWeight: minAge.details.numberOfUnderAge > minAge.details.allowedUnderAge ? "bold" : "normal" }}
                                                >
                                                    {minAge.details.numberOfUnderAge}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: "bold" }}>{t("reg.status.age_allowed_under_age")}</TableCell>
                                            <TableCell>{minAge.details.allowedUnderAge}</TableCell>
                                        </TableRow>
                                        {minAge.details.isFreestyleClass && (
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: "bold" }}>{t("reg.status.age_freestyle_class")}</TableCell>
                                                <TableCell><StatusChip value={true} t={t} /></TableCell>
                                            </TableRow>
                                        )}
                                        {minAge.details.isAdultCategory && (
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: "bold" }}>{t("reg.status.age_adult_category")}</TableCell>
                                                <TableCell><StatusChip value={true} t={t} /></TableCell>
                                            </TableRow>
                                        )}
                                        {minAge.details.memberUnder12 && (
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: "bold" }}>{t("reg.status.age_member_under_12")}</TableCell>
                                                <TableCell><StatusChip value={false} t={t} /></TableCell>
                                            </TableRow>
                                        )}
                                        {minAge.details.underAgeMembers && minAge.details.underAgeMembers.length > 0 && (
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: "bold", verticalAlign: "top" }}>{t("reg.status.age_under_age_members")}</TableCell>
                                                <TableCell>
                                                    <List dense disablePadding>
                                                        {minAge.details.underAgeMembers.map((m, i) => (
                                                            <ListItem key={i} disablePadding>
                                                                <ListItemText
                                                                    primary={`${m.name} — ${m.age} ${t("reg.status.age_years")}`}
                                                                    primaryTypographyProps={{ variant: "body2" }}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </>
                                )}

                                {!isTeam && minAge.details && !minAge.ok && minAge.details.isSenior && (
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: "bold" }}>{t("reg.status.age_senior_category")}</TableCell>
                                        <TableCell><StatusChip value={true} t={t} /></TableCell>
                                    </TableRow>
                                )}
                            </>
                        )}

                        {maxAge.limit !== null && (
                            <>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: "bold" }}>{t("reg.status.age_max_limit")}</TableCell>
                                    <TableCell>{maxAge.limit}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: "bold" }}>{t("reg.status.age_max_ok")}</TableCell>
                                    <TableCell><StatusChip value={maxAge.ok} t={t} /></TableCell>
                                </TableRow>

                                {isTeam && maxAge.details && !maxAge.ok && (
                                    <>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: "bold" }}>{t("reg.status.age_over_age_count")}</TableCell>
                                            <TableCell>
                                                <Typography
                                                    component="span"
                                                    color={maxAge.details.numberOfOverAge > maxAge.details.allowedOverAge ? "error" : "text.primary"}
                                                    sx={{ fontWeight: maxAge.details.numberOfOverAge > maxAge.details.allowedOverAge ? "bold" : "normal" }}
                                                >
                                                    {maxAge.details.numberOfOverAge}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: "bold" }}>{t("reg.status.age_allowed_over_age")}</TableCell>
                                            <TableCell>{maxAge.details.allowedOverAge}</TableCell>
                                        </TableRow>
                                        {maxAge.details.overAgeMembers && maxAge.details.overAgeMembers.length > 0 && (
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: "bold", verticalAlign: "top" }}>{t("reg.status.age_over_age_members")}</TableCell>
                                                <TableCell>
                                                    <List dense disablePadding>
                                                        {maxAge.details.overAgeMembers.map((m, i) => (
                                                            <ListItem key={i} disablePadding>
                                                                <ListItemText
                                                                    primary={`${m.name} — ${m.age} ${t("reg.status.age_years")}`}
                                                                    primaryTypographyProps={{ variant: "body2" }}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const RejectedCategoriesDialog = ({ open, onClose, uuid, api, t }) => {
    const [loading, setLoading] = useState(false);
    const [rejected, setRejected] = useState([]);
    const [fetched, setFetched] = useState(false);
    const [error, setError] = useState(false);

    const fetchRejected = () => {
        setLoading(true);
        setError(false);
        axios
            .get(`${api.getRejectedCategoriesForEntryAnonymously}${uuid}`)
            .then(function (response) {
                setRejected(response.data);
                setFetched(true);
                setLoading(false);
            })
            .catch(function (err) {
                console.log(err);
                setError(true);
                setLoading(false);
            });
    };

    const handleEnter = () => {
        setFetched(false);
        setRejected([]);
        fetchRejected();
    };

    const getFailedValidations = (status) => {
        return Object.entries(status).filter(([, value]) => !value);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            TransitionProps={{ onEnter: handleEnter }}
        >
            <DialogTitle>{t("reg.status.rejected_categories")}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    {t("reg.status.why_not_visible_info")}
                </DialogContentText>
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}
                {error && (
                    <Alert severity="error">
                        {t("reg.status.reason.unknown")}
                    </Alert>
                )}
                {fetched && !loading && rejected.length === 0 && (
                    <Alert severity="info">{t("reg.status.no_rejected")}</Alert>
                )}
                {fetched && !loading && rejected.length > 0 && (
                    <Box>
                        {rejected.map((item, index) => (
                            <Accordion key={item.category.id || index} defaultExpanded={false}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography sx={{ fontWeight: "medium" }}>
                                        {item.category.name}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {item.category.additionalDescription && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            {item.category.additionalDescription}
                                        </Typography>
                                    )}
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        {t("reg.status.failed_validations")}:
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>{t("reg.validation")}</TableCell>
                                                    <TableCell align="center">Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Object.entries(item.status).map(([key, value]) => (
                                                    <TableRow key={key}>
                                                        <TableCell>
                                                            {statusLabels[key] ? t(statusLabels[key]) : key}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <StatusChip value={value} t={t} />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    {!item.status.age && item.ageDetails && (
                                        <AgeDetailsSection ageDetails={item.ageDetails} t={t} />
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t("reg.status.close")}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default RejectedCategoriesDialog;
